import L from 'leaflet';
import { memo, useCallback, useEffect } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import { updateBrowserControlState } from '@wg/web2clientapi/browser/updateBrowserControlState';

import settings from '~/settings';
import { isWCFRequired } from '~/constants';
import { setMapControls } from '~/helpers/supply';
import useKeyDown, { KEYS_CODE } from '~/hooks/useKeyDown';
import useMount from '~/hooks/useMount';
import { RefKeys, refManager } from '~/managers/refManager';
import { useAppDispatch } from '~/store';
import { viewport } from '~/utils';
import { playClanBaseSelect } from '~/web2ClientAPI/sounds';

import { ClanBaseCameraPos } from '~/web2ClientAPI/supply';
import { actionsSupply, setSelectedBuildingThunk } from '~/Actions/ActionSupply';
import { maxBounds } from '~/Reducers/ReducerSupply';

import type React from 'react';
import type { BUILDINGS } from '~/Actions/ActionSupply';
import type { ISupplyBuildings } from '~/store/slices/settingsSlice';

const buildingsBounds: Partial<Record<BUILDINGS, L.LatLngBounds>> = {};

type IProps = {
  buildings: Nullable<ISupplyBuildings>;
  prevPosition: Nullable<L.LatLngBoundsExpression>;
  selectedBuildingId: Nullable<BUILDINGS>;
  tempHoverBuildingId: Nullable<BUILDINGS>;
  dropSelection: () => void;
};

const MapControllerLayer: React.FC<IProps> = ({
  buildings,
  prevPosition,
  selectedBuildingId,
  tempHoverBuildingId,
  dropSelection,
}) => {
  const dispatch = useAppDispatch();
  const map = useMap();

  const appRef = refManager.getRef(RefKeys.APP);

  const updateSoundPosition = () => {
    if (!map) {
      return;
    }
    const position = map.getCenter();
    const soundParams = {
      x: position.lng,
      y: position.lat,
      z: map.getZoom(),
    };
    ClanBaseCameraPos(soundParams);
  };

  const onResize = () => {
    const { width, height } = viewport();
    const isLargeScreen = width >= 2048 || height >= 1556;

    if (!isLargeScreen) {
      map.setMinZoom(settings.supply.minZoom);
      return;
    }

    const boundsZoom = map.getBoundsZoom(maxBounds, true);
    const minZoom = map.getMinZoom();

    const boundedMinZoom = Math.max(boundsZoom, minZoom);
    map.setMinZoom(boundedMinZoom);
    dispatch(actionsSupply.updateMinZoom(boundedMinZoom));
  };

  const onZoomEnd = () => {
    updateSoundPosition();

    dispatch(actionsSupply.setMapZoom(map.getZoom()));
  };

  const onMapMove = () => {
    updateSoundPosition();

    if (window.tooltipProvider?.isVisible()) {
      window.tooltipProvider.hide();
    }
  };

  const navigateToBuilding = useCallback(
    (buildingId: BUILDINGS) => {
      playClanBaseSelect();

      dispatch(actionsSupply.setHighlightedBuilding(null));

      let bounds: Nullish<L.LatLngBounds> = buildingsBounds[buildingId];

      if (!bounds) {
        const building = buildings?.[buildingId];
        const contour = building?.levels[building.level]?.geo?.contour;

        bounds = contour && L.polygon(contour).getBounds();

        if (!bounds) {
          return;
        }
      }

      map?.fitBounds(bounds, {
        maxZoom: 10,
        paddingBottomRight: [400, 200], // [sidebarWidth, headerheight]
        paddingTopLeft: [200, 200], // [mainMenuWidth, footerHeight]
        animate: true,
        duration: 0.5,
      });

      if (isWCFRequired && appRef.current) {
        if (appRef.current.scrollTop > 0) {
          appRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    },
    [appRef, buildings, dispatch, map],
  );

  useMapEvents({
    resize: onResize,
    zoomend: onZoomEnd,
    move: onMapMove,
    click: dropSelection,
  });

  useMount(() => {
    onResize();

    return () => {
      dropSelection();
    };
  });

  useKeyDown(() => {
    if (!selectedBuildingId) {
      return;
    }

    dispatch(setSelectedBuildingThunk(null));

    if (tempHoverBuildingId) {
      dispatch(actionsSupply.dropTempHoverBuildingLevel());
    }
  }, [KEYS_CODE.ESC]);

  useEffect(() => {
    void updateBrowserControlState(!!selectedBuildingId);

    if (selectedBuildingId) {
      navigateToBuilding(selectedBuildingId);
      setMapControls(false, map);
      return;
    } else {
      setMapControls(true, map);
    }

    if (prevPosition) {
      map?.fitBounds(prevPosition);
      dispatch(actionsSupply.saveMapPosition(null));
    }
  }, [selectedBuildingId, map, prevPosition, navigateToBuilding, dispatch]);

  useEffect(() => {
    if (!isWCFRequired || selectedBuildingId) {
      return;
    }

    const mapContainer = map.getContainer();
    const appContainer = appRef?.current;

    if (!mapContainer || !appContainer) {
      return;
    }

    const minZoom = map.getMinZoom();
    const maxZoom = map.getMaxZoom();

    const handleWheel = (e: WheelEvent) => {
      e.stopPropagation();

      const currentZoom = map.getZoom();
      const delta = e.deltaY > 0 ? -1 : 1;
      const currentScrollTop = appContainer.scrollTop || 0;

      if (currentScrollTop !== 0) {
        map.scrollWheelZoom.disable();
      } else {
        map.scrollWheelZoom.enable();
      }

      if (delta < 0) {
        if (currentZoom > minZoom) {
          e.preventDefault();
          map.setZoom(currentZoom + delta);
        } else {
          appContainer.scrollTo({ top: appContainer.scrollHeight, behavior: 'smooth' });
        }
      } else {
        if (currentScrollTop > 0) {
          e.preventDefault();
          appContainer.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (currentZoom < maxZoom) {
          e.preventDefault();
          map.setZoom(currentZoom + delta);
        }
      }
    };

    mapContainer.addEventListener('wheel', handleWheel);

    return () => {
      mapContainer.removeEventListener('wheel', handleWheel);
    };
  }, [appRef, map, selectedBuildingId]);

  return null;
};

export default memo(MapControllerLayer);
